import { MAX_RETRY_ATTEMPTS, OPEN_AI_CONFIG, RETRY_DELAY_MS } from './constants';
/**
 * Service for handling direct communication with Azure OpenAI API
 */
export class OpenAIApiService {
    /**
     * Creates a new instance of OpenAIApiService
     * @param apiKey The Azure OpenAI API key
     */
    constructor(apiKey) {
        Object.defineProperty(this, "apiKey", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.apiKey = apiKey;
    }
    /**
     * Sends a request to the Azure OpenAI API
     * @param params Request parameters including system prompt, user message, and context
     * @returns Promise resolving to the API response
     */
    async sendRequest(params) {
        const { systemPrompt, userMessage, context } = params;
        // Prepare messages array for the API
        const messages = [
            {
                role: 'system',
                content: systemPrompt
            }
        ];
        // Add context messages if available
        if (context && context.trim()) {
            messages.push({
                role: 'user',
                content: `Previous conversation:\n${context}`
            });
        }
        // Add the current user message
        messages.push({
            role: 'user',
            content: `Current Message of User: \n ${userMessage}`
        });
        // Prepare the request body
        const requestBody = {
            messages,
            temperature: OPEN_AI_CONFIG.temperature,
            max_tokens: OPEN_AI_CONFIG.maxTokens,
            top_p: OPEN_AI_CONFIG.topP,
            frequency_penalty: OPEN_AI_CONFIG.frequencyPenalty,
            presence_penalty: OPEN_AI_CONFIG.presencePenalty,
            stream: OPEN_AI_CONFIG.stream
        };
        // Send the request with retry logic
        return this.sendRequestWithRetry(requestBody);
    }
    /**
     * Builds the Azure OpenAI API URL
     * @returns The complete API URL
     */
    buildApiUrl() {
        return `${OPEN_AI_CONFIG.endpoint}openai/deployments/${OPEN_AI_CONFIG.deploymentName}/chat/completions?api-version=${OPEN_AI_CONFIG.apiVersion}`;
    }
    /**
     * Sends a request to the Azure OpenAI API with retry logic
     * @param requestBody The request body to send
     * @returns Promise resolving to the API response
     */
    async sendRequestWithRetry(requestBody) {
        let attempts = 0;
        while (attempts < MAX_RETRY_ATTEMPTS) {
            try {
                const apiUrl = this.buildApiUrl();
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'api-key': this.apiKey
                    },
                    body: JSON.stringify(requestBody)
                });
                if (!response.ok) {
                    const errorData = await response.json();
                    console.error('Azure OpenAI API error:', errorData);
                    throw new Error(`Azure OpenAI API error: ${response.status} ${response.statusText}`);
                }
                const data = await response.json();
                // Extract the response content and metadata
                const content = data.choices[0].message.content;
                const metadata = {
                    tokens: {
                        prompt: data.usage.prompt_tokens,
                        completion: data.usage.completion_tokens,
                        total: data.usage.total_tokens
                    },
                    model: data.model,
                    finishReason: data.choices[0].finish_reason
                };
                return { content, metadata };
            }
            catch (error) {
                attempts++;
                console.error(`Azure OpenAI API request failed (attempt ${attempts}/${MAX_RETRY_ATTEMPTS}):`, error);
                // If we've reached the maximum number of attempts, throw the error
                if (attempts >= MAX_RETRY_ATTEMPTS) {
                    throw error;
                }
                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
            }
        }
        // This should never be reached due to the throw in the loop,
        // but TypeScript requires a return statement
        throw new Error('Maximum retry attempts exceeded');
    }
    /**
     * Updates the API key
     * @param apiKey The new Azure OpenAI API key
     */
    updateApiKey(apiKey) {
        this.apiKey = apiKey;
    }
}
// Export a singleton instance with the provided API key
export const openAIApiService = new OpenAIApiService(process.env.VUE_APP_OPEN_AI_AZURE_API_KEY || '1k4gQvCjyUd4bHtkLGUWHRmMvbK4QFKYSweTOQjSaKPl0ZMOPcZFJQQJ99BDACPV0roXJ3w3AAABACOG2eau');
